import {useTheme} from '@mui/material';
import {useState} from 'react';

import {FeaturesEnum, TelehealthBookingEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {GetHealthBookingBySpecialtyParams} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthSpecialtySelection/components/HealthSpecialtiesList/hooks/useHealthSpecialtiesSelection/types';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';
import {useGetHealthSpecialtyAuthorizationMutation} from 'presentation/modules/booking/shared/mutations/useGetHealthSpecialtyAuthorizationMutation';
import {useGetHealthSpecialtiesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthSpecialtiesQuery';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';
import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';
import {useBookingConfirmationDrawer} from 'presentation/pages/private/booking/hooks/useBookingConfirmationDrawer';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {ParsedHealthAppointment} from 'presentation/pages/private/home/utils/parseHealthAppointments/types';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {useNavigate} from 'react-router-dom';
import {TypeLocalNameEnum} from 'domain/usecases';

export function useTelehealthCard() {
	const navigate = useNavigate();
	const {SEARCH_PARAMS} = useQueryParams();
	const {specialtyName, specialtyCode, hasSubSpecialty} = SEARCH_PARAMS;
	const {isEnabled: isSchedulingByMedicalGuideEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULING_BY_MEDICAL_GUIDE,
	);
	const theme = useTheme();
	const [openSimpleConfirmationDrawer, setOpenSimpleConfirmationDrawer] = useState(false);
	const [isPending, setIsPending] = useState(false);

	const {setSelectedServiceType, handleOpenBookingConfirmationDrawer} =
		useBookingConfirmationDrawer();

	const handleGoToTelehealthBooking = () => {
		setSelectedServiceType(BookingTypeEnum.TELEHEALTH);
		handleOpenBookingConfirmationDrawer();
	};

	const {currentEmail, currentPhone} = usePersonalData();
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const {PROFESSIONAL_STEP, SPECIALITY_OR_EXAM_STEP} = getTelehealthStepPosition();

	const hasSub = Boolean(hasSubSpecialty && hasSubSpecialty === 'true');

	const getHealthBookingBySpecialty = ({
		specialtyCode,
		appointments,
	}: GetHealthBookingBySpecialtyParams) => {
		return appointments?.find(
			booking =>
				booking.specialtyCode === specialtyCode &&
				(booking.bookingType === BookingTypeEnum.HEALTH ||
					booking.bookingType === BookingTypeEnum.TELEHEALTH),
		) as ParsedHealthAppointment | undefined;
	};

	const {mutateAsync: verifyHealthAuthorization} = useGetHealthSpecialtyAuthorizationMutation();
	const {refetch: refetchAppointmentsList} = useGetHomeQuery(undefined, false);
	const {refetch: refetchHealthSpecialties} = useGetHealthSpecialtiesQuery({
		isTelemedicine: true,
		enabled: false,
	});

	const configHealthStatesBeforeNavigate = async () => {
		setBookingData(prev => ({
			...prev,
			bookingOrigin: BookingOriginEnum.BOOKING_TELEHEALTH,
			bookingType: BookingTypeEnum.TELEHEALTH,
			cityName: TELEMEDICINE_INFO.cityName,
			email: currentEmail,
			phone: currentPhone,
			specialtyCode,
			specialtyName,
			typeLocalName: TypeLocalNameEnum.CIDADE,
			ufCode: TELEMEDICINE_INFO.ufCode,
			ufName: TELEMEDICINE_INFO.ufName,
			unitCode: TELEMEDICINE_INFO.code,
			unitName: TELEMEDICINE_INFO.name,
			isBookingByMedicalGuideFlow: true,
		}));
		if (specialtyCode && !hasSub) {
			try {
				const {
					content: {specialtyAuthorization},
				} = await verifyHealthAuthorization({speciality: specialtyCode});
				const {data: updatedAppointmentsList} = await refetchAppointmentsList();
				const alreadyScheduled = getHealthBookingBySpecialty({
					specialtyCode,
					appointments: updatedAppointmentsList?.appointments,
				});
				const {data: updatedHealthSpecialties} = await refetchHealthSpecialties();
				const isSpecialtyAvailable = updatedHealthSpecialties?.some(category =>
					category.list.some(specialty => specialty.code === specialtyCode),
				);
				if (alreadyScheduled || !specialtyAuthorization || !isSpecialtyAvailable) {
					setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
				} else {
					setActiveBookingFlowStep(PROFESSIONAL_STEP);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error);
				setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
			}
		} else {
			setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
		}
	};

	const handleConfirmAndContinue = async () => {
		setIsPending(true);
		try {
			await configHealthStatesBeforeNavigate();
		} finally {
			setIsPending(false);
		}
		handleOpenConfirmationDrawer();
	};

	const handleNavigateToTelehealthFlow = () => {
		navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
	};

	const handleOpenConfirmationDrawer = () => {
		setOpenSimpleConfirmationDrawer(true);
	};

	const handleCloseConfirmationDrawer = () => {
		setOpenSimpleConfirmationDrawer(false);
	};

	return {
		handleCloseConfirmationDrawer,
		handleOpenConfirmationDrawer,
		handleNavigateToTelehealthFlow,
		handleConfirmAndContinue,
		handleGoToTelehealthBooking,
		isSchedulingByMedicalGuideEnabled,
		isPending,
		openSimpleConfirmationDrawer,
		theme,
	};
}
